// src/pages/DocumentsPage.js
// Updated: 10/31/2025 09:12 PM EDT (admin+reviewer guidance parity; chunked `in` queries; resilience)
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  documentId,
} from 'firebase/firestore';
import { Plus } from 'lucide-react';

import FileUploadModal from '../components/FileUploadModal';
import DocumentList from '../components/DocumentList';

export default function DocumentsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { userData, currentUser } = useAuth();
  const isAdmin = userData?.role === 'admin';

  // Helper: chunk an array into groups of size n (Firestore `in` max = 10)
  const chunkArray = (arr, size = 10) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  };

  useEffect(() => {
    setError('');

    if (!userData?.orgId || !currentUser?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // --- ADMIN: Keep the simple real-time admin view (all org docs), BUT also enrich with session names ---
    // If you prefer admins to ONLY see open-session guidance (not closed), comment this admin block
    // and let the unified fetch below run for all roles.
    if (isAdmin) {
      const q = query(
        collection(db, 'documents'),
        where('orgId', '==', userData.orgId),
        orderBy('uploadedAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          try {
            const docs = [];
            querySnapshot.forEach((docSnap) => {
              docs.push({ id: docSnap.id, ...docSnap.data() });
            });

            // Enrich guidance docs with session names for nicer UI
            const guidanceSessionIds = Array.from(
              new Set(
                docs
                  .filter((d) => d?.context === 'guidance' && d?.linkedSessionId)
                  .map((d) => d.linkedSessionId)
              )
            );
            let sessionNameMap = new Map();
            if (guidanceSessionIds.length) {
              for (const chunk of chunkArray(guidanceSessionIds, 10)) {
                const sQ = query(
                  collection(db, 'scoringSessions'),
                  where(documentId(), 'in', chunk)
                );
                const sSnap = await getDocs(sQ);
                sSnap.forEach((s) => {
                  const name = s.data()?.name || 'Unknown Session';
                  sessionNameMap.set(s.id, name);
                });
              }
            }

            const enriched = docs.map((d) =>
              d?.context === 'guidance'
                ? { ...d, linkedSessionName: sessionNameMap.get(d.linkedSessionId) || 'Unknown Session' }
                : d
            );

            setDocuments(enriched);
            setIsLoading(false);
          } catch (e) {
            console.error('Admin snapshot enrich error:', e);
            setError('Failed to enrich documents.');
            setIsLoading(false);
          }
        },
        (fetchError) => {
          console.error('Error fetching documents for admin:', fetchError);
          setError('Failed to load documents for administration.');
          setIsLoading(false);
        }
      );
      return () => unsubscribe();
    }

    // --- NON-ADMIN (reviewer/member): Public + packages + guidance (open sessions only) ---
    const fetchAllVisibleDocs = async () => {
      try {
        const orgId = userData.orgId;
        const userId = currentUser.uid;

        const allDocsMap = new Map();

        // Step 1: Open sessions
        // - Admin: (handled above)
        // - Reviewer: only sessions where the user is assigned
        const sessionsQuery = query(
          collection(db, 'scoringSessions'),
          where('orgId', '==', orgId),
          where('status', '==', 'open'),
          where('reviewers', 'array-contains', userId)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);

        const sessionNameMap = new Map();
        const openSessionIds = new Set();
        sessionsSnapshot.forEach((docSnap) => {
          const d = docSnap.data();
          sessionNameMap.set(docSnap.id, d?.name || 'Unknown Session');
          openSessionIds.add(docSnap.id);
        });

        // Step 2: Public documents
        const publicDocsQuery = query(
          collection(db, 'documents'),
          where('orgId', '==', orgId),
          where('isPublic', '==', true)
        );
        (await getDocs(publicDocsQuery)).forEach((docSnap) => {
          allDocsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data(), context: 'public' });
        });

        // Step 3: Scoring docs via packages
        const scoringDocIdsToFetch = new Set();
        for (const sessionId of openSessionIds) {
          const packagesQuery = query(collection(db, 'scoringSessions', sessionId, 'packages'));
          const packagesSnapshot = await getDocs(packagesQuery);
          packagesSnapshot.forEach((pkgDoc) => {
            const docId = pkgDoc.data()?.linkedDocumentId;
            if (docId) scoringDocIdsToFetch.add(docId);
          });
        }

        if (scoringDocIdsToFetch.size > 0) {
          for (const idChunk of chunkArray(Array.from(scoringDocIdsToFetch), 10)) {
            const docsQuery = query(collection(db, 'documents'), where(documentId(), 'in', idChunk));
            (await getDocs(docsQuery)).forEach((docSnap) => {
              allDocsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data(), context: 'scoring' });
            });
          }
        }

        // Step 4: Guidance docs (OPEN sessions only)
        if (openSessionIds.size > 0) {
          for (const sessionChunk of chunkArray(Array.from(openSessionIds), 10)) {
            const base = [
              where('orgId', '==', orgId),
              where('context', '==', 'guidance'),
              where('linkedSessionId', 'in', sessionChunk),
            ];
            // Reviewer must also be assigned
            const guidanceDocsQuery = query(collection(db, 'documents'), ...base, where('assignedReviewers', 'array-contains', userId));
            const guidanceSnap = await getDocs(guidanceDocsQuery);
            guidanceSnap.forEach((docSnap) => {
              const docData = docSnap.data();
              allDocsMap.set(docSnap.id, {
                id: docSnap.id,
                ...docData,
                context: 'guidance',
                linkedSessionName: sessionNameMap.get(docData?.linkedSessionId) || 'Unknown Session',
              });
            });
          }
        }

        const finalDocs = Array.from(allDocsMap.values()).sort(
          (a, b) => (b.uploadedAt?.toMillis?.() || 0) - (a.uploadedAt?.toMillis?.() || 0)
        );

        setDocuments(finalDocs);
      } catch (fetchError) {
        console.error('Error fetching combined documents for non-admin:', fetchError);
        setError('Failed to load documents.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllVisibleDocs();
    return () => {};
  }, [userData, currentUser, isAdmin]);

  return (
    <>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
        {/* Only Admins can upload general documents now */}
        {isAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
          >
            <Plus className="mr-2 h-5 w-5" />
            Upload Document
          </button>
        )}
      </div>

      <div className="mt-6">
        {error && (
          <div className="mb-4 p-3 rounded-md border border-red-400 bg-red-50 text-red-700">
            Error: {error}
          </div>
        )}

        {isLoading ? <p>Loading documents...</p> : <DocumentList documents={documents} />}
      </div>

      <FileUploadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
